import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { notificationsApi } from '@/services/endpoints';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  createdAt: string;
  updatedAt: string;
  userId: string;
  data?: string;
}

export const useNotificationsStore = defineStore('notifications', () => {
  // State
  const notifications = ref<Notification[]>([]);
  const unreadCount = ref(0);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const lastFetch = ref<Date | null>(null);

  // Computed
  const unreadNotifications = computed(() => 
    notifications.value.filter(n => !n.read)
  );

  const readNotifications = computed(() => 
    notifications.value.filter(n => n.read)
  );

  const recentNotifications = computed(() => 
    notifications.value
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10)
  );

  // Actions
  // Keep your existing Notification interface
  
  // In your fetch function, transform the API data:
  const fetchNotifications = async () => {
    try {
      loading.value = true;
      error.value = null;
      
      const response = await notificationsApi.list();
      
      // Transform API notifications to store format
      notifications.value = response.data.map((apiNotification: any) => ({
        id: apiNotification.id.toString(),
        title: apiNotification.title,
        message: apiNotification.message,
        type: apiNotification.type,
        read: apiNotification.is_read,
        createdAt: apiNotification.created_at,
        updatedAt: apiNotification.updated_at,
        userId: apiNotification.user_id.toString(),
        data: apiNotification.data
      }));
      
      unreadCount.value = unreadNotifications.value.length;
      lastFetch.value = new Date();
    } catch (err) {
      error.value = 'Erro ao carregar notificações';
      console.error('Error fetching notifications:', err);
    } finally {
      loading.value = false;
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await notificationsApi.markAsRead(notificationId);
      
      const notification = notifications.value.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        notification.read = true;
        unreadCount.value = Math.max(0, unreadCount.value - 1);
      }
    } catch (err) {
      error.value = 'Erro ao marcar notificação como lida';
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      
      notifications.value.forEach(notification => {
        notification.read = true;
      });
      unreadCount.value = 0;
    } catch (err) {
      error.value = 'Erro ao marcar todas as notificações como lidas';
      console.error('Error marking all notifications as read:', err);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      await notificationsApi.delete(notificationId);
      
      const index = notifications.value.findIndex(n => n.id === notificationId);
      if (index > -1) {
        const notification = notifications.value[index];
        if (!notification.read) {
          unreadCount.value = Math.max(0, unreadCount.value - 1);
        }
        notifications.value.splice(index, 1);
      }
    } catch (err) {
      error.value = 'Erro ao excluir notificação';
      console.error('Error deleting notification:', err);
    }
  };

  // Helper function to transform API notification to store format
  const transformApiNotification = (apiNotification: any): Notification => ({
    id: apiNotification.id.toString(),
    title: apiNotification.title,
    message: apiNotification.message,
    type: apiNotification.type,
    read: apiNotification.is_read,
    createdAt: apiNotification.created_at,
    updatedAt: apiNotification.updated_at,
    userId: apiNotification.user_id.toString(),
    data: apiNotification.data
  });
  
  // Then use it in createNotification:
  const createNotification = async (notificationData: Omit<Notification, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {       
      const response = await notificationsApi.send(notificationData);
      const transformedNotification = transformApiNotification(response);
      
      notifications.value.unshift(transformedNotification);
      
      if (!transformedNotification.read) {
        unreadCount.value += 1;
      }
      
      return transformedNotification;
    } catch (err) {
      error.value = 'Erro ao criar notificação';
      console.error('Error creating notification:', err);
      throw err;
    }
  };

  const getUnreadCount = async () => {
    try {
      const count = await notificationsApi.getUnreadCount();
      unreadCount.value = count;
      return count;
    } catch (err) {
      console.error('Error getting unread count:', err);
      return 0;
    }
  };

  const addNotification = (notification: Notification) => {
    notifications.value.unshift(notification);
    if (!notification.read) {
      unreadCount.value += 1;
    }
  };

  const updateNotification = (notificationId: string, updates: Partial<Notification>) => {
    const index = notifications.value.findIndex(n => n.id === notificationId);
    if (index > -1) {
      const notification = notifications.value[index];
      const wasUnread = !notification.read;
      
      Object.assign(notification, updates);
      
      // Update unread count if read status changed
      if (wasUnread && notification.read) {
        unreadCount.value = Math.max(0, unreadCount.value - 1);
      } else if (!wasUnread && !notification.read) {
        unreadCount.value += 1;
      }
    }
  };

  const clearNotifications = () => {
    notifications.value = [];
    unreadCount.value = 0;
    error.value = null;
  };

  const clearAll = async () => {
    try {
      await notificationsApi.deleteAll();
      notifications.value = [];
      unreadCount.value = 0;
      error.value = null;
    } catch (err) {
      error.value = 'Erro ao limpar todas as notificações';
      console.error('Error clearing all notifications:', err);
      throw err;
    }
  };

  const clearError = () => {
    error.value = null;
  };

  // Auto-refresh notifications every 30 seconds
  const startAutoRefresh = () => {
    const interval = setInterval(() => {
      if (!loading.value) {
        getUnreadCount();
      }
    }, 30000);

    return () => clearInterval(interval);
  };

  return {
    // State
    notifications,
    unreadCount,
    loading,
    error,
    lastFetch,
    
    // Computed
    unreadNotifications,
    readNotifications,
    recentNotifications,
    
    // Actions
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    createNotification,
    getUnreadCount,
    addNotification,
    updateNotification,
    clearNotifications,
    clearAll,
    clearError,
    startAutoRefresh
  };
});